import { useCallback, useState } from 'react';
import {
  useGeneratePublicTaskLinksMutation,
  useSendReminderEmailsMutation,
} from '@/modules/admin-dashboard/queries/tasks.ts';
import { BaseModalProps, Button, CustomCheckbox, ModalContentWrapper, Spinner } from 'ui';
import { copyTextToClipBoard } from 'ui/utils';
import { PublicLinkResponse, TaskListWithRelationsResponse, User } from 'compliance-api';

interface RemindAssigneeModalProps extends BaseModalProps {
  task: TaskListWithRelationsResponse;
}

const RemindAssigneeModal = ({ task, open, onClose }: RemindAssigneeModalProps) => {
  const [selectedUsersIDs, setSelectedUsersIDs] = useState<number[]>([]);
  const [publicLinks, setPublicLinks] = useState<PublicLinkResponse[]>();

  const { mutateAsync: generatePublicLinks, isLoading: isPublicLinksGeneration } =
    useGeneratePublicTaskLinksMutation();

  const { mutateAsync: sendReminders, isLoading: isSendingReminders } =
    useSendReminderEmailsMutation();

  const handleToggleSelection = useCallback((id: number, checked: boolean) => {
    setSelectedUsersIDs(ids => (checked ? [...ids, id] : ids.filter(idx => idx !== id)));
  }, []);

  const handleSendReminderEmails = useCallback(async () => {
    await sendReminders({
      taskId: task.id,
      usersIDs: selectedUsersIDs,
    });

    onClose();
  }, [onClose, selectedUsersIDs, sendReminders, task.id]);

  const handleCopyMagicLink = useCallback(
    async (userId: number) => {
      let resolveLinks = publicLinks;

      if (!publicLinks) {
        const res = await generatePublicLinks({
          taskId: task.id,
          usersIDs: (task.users || []).map(({ id }) => id),
        });

        setPublicLinks(res.public_link_responses);
        resolveLinks = res.public_link_responses;
      }
      const data = (resolveLinks || []).find(data => data.user_id === userId);

      if (data) {
        copyTextToClipBoard(data.link, { successMessage: 'Authorisation link copied' });
      }
    },
    [generatePublicLinks, publicLinks, task.id, task.users],
  );

  return (
    <ModalContentWrapper title="Remind assignee" open={open} onClose={onClose}>
      <div className="mb-4">
        <div className="text-sm text-gray-500">Task</div>
        <div className="font-medium break-words">{task.name}</div>
      </div>
      <div className="space-y-1">
        {(task.users || []).map((user: User) => (
          <div key={user.id} className="flex gap-2 items-center">
            <CustomCheckbox
              value={user.id}
              checked={selectedUsersIDs.includes(user.id)}
              onChange={handleToggleSelection.bind(
                null,
                user.id,
                !selectedUsersIDs.includes(user.id),
              )}
            />
            <div
              className="flex-1 hover:underline cursor-pointer"
              onClick={handleToggleSelection.bind(
                null,
                user.id,
                !selectedUsersIDs.includes(user.id),
              )}
            >
              {user.first_name} {user.last_name}
            </div>
            {isPublicLinksGeneration ? (
              <Spinner size="xs" />
            ) : (
              <button
                className="text-blue-700 hover:underline"
                onClick={handleCopyMagicLink.bind(null, user.id)}
              >
                Copy link
              </button>
            )}
          </div>
        ))}
      </div>
      <Button
        className="mt-3"
        disabled={!selectedUsersIDs.length || isSendingReminders}
        loading={isSendingReminders}
        onClick={handleSendReminderEmails}
      >
        Send emails
      </Button>
    </ModalContentWrapper>
  );
};

export default RemindAssigneeModal;
